import { useState } from 'react';

import { Button } from '@clay-run/glaze/button';
import {
  CollapsibleCard,
  CollapsibleCardBody,
  CollapsibleCardHeader,
} from '@clay-run/glaze/collapsible-card';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Switch } from '@clay-run/glaze/switch';
import { Text } from '@clay-run/glaze/text';
import { addToast } from '@clay-run/glaze/toast/toast-queue';

import { FilterPopover } from '../../../_shared/components/flow';
import { CAMPAIGN_MEMBER_FILTER_FIELDS } from '../../campaign-member-filter-fields';
import {
  selectAudienceDraft,
  setAudienceDraft,
  setRungScope,
  useRungScopeStore,
} from '../../rung-scope-store';

/**
 * Batch 9 B1 — the "Audience Rules" step, DISABLED as a 4th `PlayFlowShell`
 * tab behind a "Coming soon" tooltip (plan Decision #3). This is a straight
 * extraction of `QualifyStep.tsx`'s old `AudienceRuleCard` (baseline
 * `QualifyStep.tsx:217-347`) into its own file — the component itself is
 * UNCHANGED, still live and type-checked against `rung-scope-store` and
 * `FilterPopover`, so re-enabling the tab later is a one-line change in
 * `play-flow-step.ts` (move its id from `COMING_SOON_TABS` into
 * `PLAY_FLOW_STEPS`) plus removing the `disabled`/`Tooltip` wrapper in
 * `PlayFlowShell.tsx` — no component rewrite needed.
 *
 * The dormant preview-coupling stays intact and inert (Decision #3): this
 * still reads/writes `rung-scope-store`'s audience draft, and "Preview
 * matches" still calls `setRungScope({ kind: 'audience' })`, exactly as it
 * did inside `QualifyStep`. Nothing here is reachable today because the tab
 * hosting it is disabled and `?step=audience-rules` isn't a valid step id
 * (see `play-flow-step.ts`), so this coupling is dead code path, not a live
 * behavior — kept only so the file compiles and stays in sync with the
 * store's real shape.
 */
export function AudienceRulesStep() {
  // The draft lives in `rung-scope-store` rather than this component's own
  // `useDraftState`, because `MembersPanel` — a SIBLING mounted by
  // `PlayFlowShell`, not a descendant of this card — has to preview exactly the
  // filter this card is editing. `useDraftState` is component-local `useState`
  // underneath, so calling it with the same key in both places would produce
  // two independent drafts that quietly disagree. The store also resets per
  // `playId` (PlayFlowShell's mount effect), which is the freshness property
  // the `useDraftState(undefined, playId)` keying gave us before.
  const value = useRungScopeStore(selectAudienceDraft);
  // Collapsed by default (QA item 1): prototype default verified —
  // `audOpen: false` (`playbooks-prototype-v6.html:2887`), and the Round-7B
  // decision comment (`:578`) documents the flip from `true` to `false`
  // explicitly. `WaterfallCard`'s own `open` state (below) stays `true` —
  // Segmentations is unaffected by this item.
  const [open, setOpen] = useState(false);

  return (
    <div className="p-6">
      <CollapsibleCard open={open} onOpenChange={setOpen} className="border-border-primary">
        <CollapsibleCardHeader>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <Text size="sm" weight="semibold">
              Audience Rule
            </Text>
            <Text size="xs" variant="tertiary">
              Keep matching members as they enter the campaign, instead of routing one fixed roster.
            </Text>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {value.enabled ? (
              <StatusBadge status="success">On</StatusBadge>
            ) : (
              <StatusBadge status="neutral">Off</StatusBadge>
            )}
            <Switch
              checked={value.enabled}
              onCheckedChange={(enabled: boolean) => setAudienceDraft({ enabled })}
              aria-label="Toggle Audience Rule"
            />
          </div>
        </CollapsibleCardHeader>
        <CollapsibleCardBody className="flex flex-col gap-3 p-4">
          <Text size="xs" variant="tertiary">
            Qualification criteria — staged here only. Apply the filters, then Preview matches to see the
            real members they match in the table above. Persisting this rule ships in a later PR.
          </Text>
          {/*
            R15 (PR B U5 follow-up, R1): `FilterPopover` renders no border or
            background of its own — it's normally hosted inside
            `ResponsivePopover` (Flow's own host), which supplies popover chrome
            (`ResponsivePopover.tsx` ~line 182: `rounded-md border
            border-secondary bg-white shadow-popover` + dark-mode equivalents).
            Embedded directly here (not inside a popover), this wrapper used to
            reproduce that popover chrome locally. Spencer's QA flagged the
            mismatch: every other v2 box (`ContextCard`, `CoverageCard`,
            `SequencesStep`/`ReviewStep` cards) uses the flat reference card
            look (`border-border-primary bg-bg-primary`, no shadow), so this
            wrapper now matches that instead of reproducing a floating popover
            look. `FilterPopover.tsx` itself is untouched.

            R14: `FilterPopover`'s "Apply filters" button hardcodes
            `bg-content-action text-white hover:bg-content-action-hover`.
            `--color-content-action` is a FOREGROUND (text/icon) token —
            Terra's `index.css` deliberately gives it a pale, readable-as-TEXT
            dark-mode value (`--color-blueberry-200`), the same pattern as
            `content-danger`/`content-link`. Used as a BACKGROUND (as this one
            button does), that pale value is exactly the washed-out look
            Spencer's QA flagged: this is a misused token, not a bad token
            definition, and it reproduces identically wherever
            `FilterPopover` renders (the class is hardcoded, not
            ancestor-dependent) — including Flow's own dark-mode filter
            popover, so a `FilterPopover.tsx` button-class fix is the
            "real" fix but needs Flow regression QA this plan can't reach
            (Risks section). Scoped to just this embed instead: redeclaring
            `--color-content-action`/`--color-content-action-hover` on this
            wrapper remaps them, for this subtree only, to Terra's actual
            solid-background action tokens (`--color-bg-action-primary`/
            `-hover`, mode-invariant blueberry-600/700) — semantic tokens
            only, no raw color. Side effect, contained to dark mode only
            (light mode already resolves `--color-content-action` to the same
            blueberry-600, so this remap is a light-mode no-op): every OTHER
            `text-content-action` usage inside this one embed (the
            "Where"/"And"/"Or" group-logic toggle, "Filter"/"Filter group"
            hover text) also picks up the darker, less-pale blue in dark mode.
            A minor, contained cosmetic change scoped to this one screen —
            Spencer's QA re-check should eyeball it, not a functional
            regression.
          */}
          <div
            className="rounded-md border border-border-primary bg-bg-primary p-3 [--color-content-action:var(--color-bg-action-primary)] [--color-content-action-hover:var(--color-bg-action-primary-hover)]"
          >
            <FilterPopover
              filter={value.groups}
              onFiltersChange={(groups) => setAudienceDraft({ groups })}
              groupLogic={value.groupLogic}
              onGroupLogicChange={(groupLogic) => setAudienceDraft({ groupLogic })}
              fields={CAMPAIGN_MEMBER_FILTER_FIELDS}
            />
          </div>
          <div className="flex justify-end gap-2">
            {/*
              Preview is the real affordance now (U3/R4): it scopes `MembersPanel`
              to this draft, which runs it against real campaign members. "Save"
              stays a stub — persistence is Phase 1 — so the two are separate
              buttons rather than one control that previews and pretends to save.
            */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setRungScope({ kind: 'audience' })}
            >
              Preview matches
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setRungScope({ kind: 'audience' });
                addToast({
                  content: "Audience rule editing isn't persisted yet — ships in a later PR.",
                  status: 'info',
                });
              }}
            >
              Save audience rule
            </Button>
          </div>
        </CollapsibleCardBody>
      </CollapsibleCard>
    </div>
  );
}
